import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heart, MessageSquare, Send, MapPin, ShieldCheck,
  Award, Wrench, Package, Boxes, Clock, Euro, Sparkles, CheckCircle2,
  TrendingUp, CalendarClock, UserCheck, Lock
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import {
  Card, Chip, StatusDot, Button, CompatibilityRing,
  MatchScoreBreakdown, VerifiedBadge, ReputationBadge, Toggle
} from "../components/Primitives";
import { getProducer, createOrGetChat, markProfileViewed, requestProfileAccess, getMyAccessRequest } from "../lib/db";

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-electric-400" />
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function reputationLevel(score) {
  if (score >= 90) return "expert";
  if (score >= 75) return "established";
  if (score >= 60) return "rising";
  return "new";
}

export default function ProducerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [fav, setFav] = useState(false);
  const [analyze, setAnalyze] = useState(false);
  const [starting, setStarting] = useState(false);
  const [accessReq, setAccessReq] = useState(null);
  const [requestingAccess, setRequestingAccess] = useState(false);

  useEffect(() => {
    let alive = true;
    getProducer(id).then((row) => {
      if (!alive) return;
      setP(row);
      // Persistently mark this profile as viewed for the current user (once loaded,
      // and only if it is not the user's own profile).
      if (user?.id && row?.id && user.id !== row.id) {
        markProfileViewed(user.id, row.id).finally(() => {
          window.dispatchEvent(new Event("r4pro:refresh-nav"));
        });
      }
    });
    return () => { alive = false; };
  }, [id, user?.id]);

  // Load current access-request state for private profiles the viewer does not own.
  useEffect(() => {
    let alive = true;
    if (!user?.id || !p?.id) return;
    if (p.hasFullAccess || p.visibility !== "private" || p.id === user.id) { setAccessReq(null); return; }
    getMyAccessRequest(user.id, p.id).then((r) => { if (alive) setAccessReq(r); });
    return () => { alive = false; };
  }, [user?.id, p?.id, p?.visibility, p?.hasFullAccess]);

  const askAccess = async () => {
    if (!user?.id || !p?.id || requestingAccess) return;
    setRequestingAccess(true);
    try {
      const r = await requestProfileAccess(user.id, p.id);
      setAccessReq(r);
      toast.success(t("visibility.requestSent"));
    } catch (e) {
      toast.error(e?.message || "Could not send request");
    } finally {
      setRequestingAccess(false);
    }
  };

  const startChat = async () => {
    if (!user?.id) { toast.error("Please sign in"); return; }
    if (!p?.id) return;
    if (p.id === user.id) { toast.info("This is your own profile"); return; }
    setStarting(true);
    try {
      await createOrGetChat(user.id, p.id);
      navigate("/app/messages");
    } catch (e) {
      toast.error(e?.message || "Could not open chat");
    } finally {
      setStarting(false);
    }
  };

  if (!p) {
    return (
      <div className="p-8 max-w-3xl mx-auto animate-fade-up">
        <div className="text-ink-500 font-mono text-xs">Loading profile…</div>
      </div>
    );
  }

  // Private preview — shown when the viewer does not have full access to a
  // private profile. Only capability-safe fields are rendered (name, bio,
  // reputation, response stats, portfolio and certifications are NOT).
  if (!p.hasFullAccess) {
    const displayTitle = (p.skills && p.skills[0]) || (p.processes && p.processes[0]) || t("visibility.badge");
    const status = accessReq?.status;
    const btnLabel =
      status === "pending"   ? t("visibility.requestPending") :
      status === "approved"  ? t("visibility.requestApproved") :
      status === "denied"    ? t("visibility.requestDenied")  :
                               t("visibility.requestAccess");
    return (
      <div className="animate-fade-up" data-testid="producer-profile-private">
        <div className="relative h-40 sm:h-56 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-carbon-900 via-carbon-900/70 to-transparent" />
          <button onClick={() => navigate(-1)} data-testid="producer-back"
            className="absolute top-4 left-4 w-9 h-9 rounded-full bg-carbon-900/60 backdrop-blur border border-carbon-600 flex items-center justify-center hover:bg-carbon-800">
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 md:p-8 max-w-3xl mx-auto -mt-12 relative">
          <Card className="p-5 mb-6" data-testid="private-preview-card">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-carbon-800 border border-carbon-600 flex items-center justify-center text-ink-500 shrink-0">
                <Lock className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">{displayTitle}</h1>
                  <Chip className="!bg-electric-500/10 !border-electric-500/40 !text-electric-600 !text-[10px]" data-testid="private-badge">
                    <Lock className="w-3 h-3" /> {t("visibility.badge")}
                  </Chip>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-ink-600 mt-2 flex-wrap">
                  {p.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</span>}
                  <span className="flex items-center gap-1"><StatusDot status={p.availability} /> {t(`availability.${p.availability}`)}</span>
                  <span className="font-mono uppercase tracking-widest">{p.userType}</span>
                </div>
                <p className="text-[11px] text-ink-500 mt-3 leading-relaxed">{t("visibility.previewSub")}</p>
              </div>
            </div>
          </Card>

          {(p.skills?.length || p.materials?.length || p.processes?.length) ? (
            <div className="mb-6">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
                {t("visibility.previewTitle")}
              </div>
              <Card className="p-5 space-y-4">
                {p.processes?.length > 0 && (
                  <Section title={t("capabilities.processes")} icon={Wrench}>
                    <div className="flex flex-wrap gap-1.5">
                      {p.processes.map((s) => <Chip key={`proc-${s}`}>{s}</Chip>)}
                    </div>
                  </Section>
                )}
                {p.skills?.length > 0 && (
                  <Section title={t("producer.skills")} icon={Wrench}>
                    <div className="flex flex-wrap gap-1.5">
                      {p.skills.map((s) => <Chip key={`sk-${s}`}>{s}</Chip>)}
                    </div>
                  </Section>
                )}
                {p.materials?.length > 0 && (
                  <Section title={t("producer.materials")} icon={Package}>
                    <div className="flex flex-wrap gap-1.5">
                      {p.materials.map((m) => <Chip key={`m-${m}`}>{m}</Chip>)}
                    </div>
                  </Section>
                )}
                {p.equipment?.length > 0 && (
                  <Section title={t("capabilities.equipment")} icon={Boxes}>
                    <div className="space-y-1.5">
                      {p.equipment.map((e, i) => (
                        <div key={`eq-${i}`} className="text-xs text-ink-700 font-mono pl-3 border-l-2 border-electric-500/40">
                          {e?.name || "—"}{e?.process ? ` · ${e.process}` : ""}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </Card>
            </div>
          ) : null}

          <Card className="p-5 mb-24" data-testid="private-locked-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-carbon-800 border border-carbon-600 flex items-center justify-center text-ink-500 shrink-0">
                <Lock className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-900">{t("visibility.lockedTitle")}</div>
                <p className="text-[11px] text-ink-500 mt-1 leading-relaxed">{t("visibility.lockedBody")}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-30 bg-carbon-900/90 backdrop-blur-xl border-t border-carbon-600 p-3">
          <div className="max-w-4xl mx-auto flex gap-2">
            <Button className="flex-1" data-testid="producer-request-access"
              onClick={askAccess} disabled={requestingAccess || !!accessReq}>
              <Lock className="w-4 h-4" />
              {btnLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const trustItems = [
    { icon: CheckCircle2, label: t("producer.responseRate"), value: `${p.responseRate}%` },
    { icon: Clock, label: t("producer.responseTime"), value: `${p.responseTimeH}h` },
    { icon: TrendingUp, label: t("producer.onTime"), value: `${p.onTimeRate}%` },
    { icon: Sparkles, label: t("producer.completed"), value: `${p.projectsCompleted}` },
  ];

  const reputationFactors = [
    { key: "completed", value: `${p.projectsCompleted}` },
    { key: "response", value: `${p.responseRate}%` },
    { key: "reliability", value: `${p.onTimeRate}%` },
    { key: "completeness", value: `${p.profileCompleteness}%` },
  ];

  return (
    <div className="animate-fade-up" data-testid="producer-profile">
      {/* Hero */}
      <div className="relative h-56 sm:h-72 overflow-hidden">
        {p.portfolio?.[0] && (
          <img src={p.portfolio[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-carbon-900 via-carbon-900/70 to-transparent" />
        <button
          onClick={() => navigate(-1)}
          data-testid="producer-back"
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-carbon-900/60 backdrop-blur border border-carbon-600 flex items-center justify-center hover:bg-carbon-800"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => { setFav(!fav); toast.success(fav ? "Rimosso dai preferiti" : "Aggiunto ai preferiti"); }}
          data-testid="producer-fav"
          className={`absolute top-4 right-4 w-9 h-9 rounded-full backdrop-blur border flex items-center justify-center transition ${
            fav ? "bg-electric-500/20 border-electric-500 text-electric-400" : "bg-carbon-900/60 border-carbon-600"
          }`}
        >
          <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
        </button>
      </div>

      <div className="p-4 md:p-8 max-w-5xl mx-auto -mt-16 relative">
        {/* Identity row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 mb-6">
          <div className="w-20 h-20 rounded-2xl bg-carbon-800 border border-carbon-600 flex items-center justify-center font-mono text-electric-400 text-2xl shadow-xl shrink-0">
            {p.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0 pb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight truncate">{p.name}</h1>
              {p.verified && <VerifiedBadge label={t("producer.verified")} />}
              <Chip className="!bg-carbon-800 !border-carbon-500 !text-ink-800">
                <UserCheck className="w-3 h-3" /> {p.userType}
              </Chip>
            </div>
            <p className="text-ink-700 text-sm mt-1">{p.positioning}</p>
            <div className="flex items-center gap-3 text-[11px] text-ink-600 mt-2 flex-wrap">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</span>
              <span className="font-mono">{p.distanceKm} km</span>
              <span className="flex items-center gap-1"><StatusDot status={p.availability} /> {t(`availability.${p.availability}`)}</span>
            </div>
          </div>
        </div>

        {/* REPUTATION CARD — distinct from Match Score, earned over time */}
        <Card className="p-5 mb-6" data-testid="reputation-card">
          <div className="flex items-start gap-5 mb-5">
            {p.reputation > 0 ? (
              <div className="w-24 h-24 rounded-2xl bg-[#F5B93B]/10 border border-[#F5B93B]/40 flex flex-col items-center justify-center shrink-0">
                <span className="font-mono font-bold text-3xl text-[#F5B93B]">{p.reputation}</span>
                <span className="text-[9px] font-mono text-[#F5B93B]/80 tracking-widest uppercase">
                  {t(`reputation.levels.${reputationLevel(p.reputation)}`)}
                </span>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-carbon-750 border border-carbon-600 flex flex-col items-center justify-center shrink-0" data-testid="reputation-card-empty">
                <Award className="w-6 h-6 text-ink-500 mb-1" />
                <span className="text-[9px] font-mono text-ink-600 tracking-widest uppercase text-center leading-tight px-1">
                  {t("reputation.newMember")}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Award className="w-3.5 h-3.5 text-[#F5B93B]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B93B]">
                  {t("reputation.label")}
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold tracking-tight">
                {t("producer.reputation")}
              </h2>
              <p className="text-xs text-ink-700 mt-1 leading-relaxed">
                {p.reputation > 0 ? t("producer.reputationSub") : t("reputation.noCompletedCollabs")}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-carbon-600">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-600 mb-3">
              {t("reputation.builtOn")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {reputationFactors.map((f) => (
                <div key={f.key} className="p-3 rounded-lg bg-carbon-900/70 border border-carbon-600">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">
                    {t(`reputation.factors.${f.key}`)}
                  </div>
                  <div className="text-base font-semibold font-mono text-ink-900">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* MATCH SCORE — OPTIONAL, opt-in only */}
        <Card className="p-5 mb-6" data-testid="match-score-card">
          <div className="mb-3">
            <Toggle
              checked={analyze}
              onChange={setAnalyze}
              label={
                <span className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-electric-400" />
                  {t("matchScore.title")}
                </span>
              }
              help={analyze ? t("matchScore.onHelp") : t("matchScore.offHelp")}
              testId="profile-analyze-toggle"
            />
          </div>

          {analyze && (
            <div className="mt-5 pt-5 border-t border-carbon-600 animate-fade-up">
              <div className="flex items-start gap-5 mb-4">
                <CompatibilityRing score={p.compatibility} size={80} stroke={6} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-electric-400">
                      COMPATIBILITY
                    </span>
                  </div>
                  <h3 className="text-base font-semibold tracking-tight">
                    {t("producer.matchScoreTitle")}
                  </h3>
                  <p className="text-xs text-ink-600 mt-1 leading-relaxed">
                    {t("producer.matchScoreSub")}
                  </p>
                </div>
              </div>

              <MatchScoreBreakdown
                reasons={p.matchReasons.map((r) => ({
                  score: r.score,
                  label: t(`matchScore.${r.label.toLowerCase()}`),
                }))}
              />
            </div>
          )}
        </Card>

        {/* Trust indicators */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-electric-400" />
            <h3 className="text-sm font-semibold tracking-tight">{t("producer.trustIndicators")}</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {trustItems.map((tItem) => (
              <Card key={tItem.label} className="p-4">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">
                  <tItem.icon className="w-3 h-3 text-electric-400" />
                  {tItem.label}
                </div>
                <div className="text-lg font-semibold font-mono text-ink-900">{tItem.value}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">
              <Clock className="w-3 h-3" /> {t("producer.leadTime")}
            </div>
            <div className="text-lg font-semibold font-mono">{p.leadTimeDays} <span className="text-xs text-ink-600">days</span></div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">
              <Euro className="w-3 h-3" /> {t("producer.rate")}
            </div>
            <div className="text-lg font-semibold font-mono">{p.rateFrom}<span className="text-xs text-ink-600">/h</span></div>
          </Card>
          <Card className="p-4 sm:col-span-1 col-span-2">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1">
              <CalendarClock className="w-3 h-3" /> {t("producer.yearsActive")}
            </div>
            <div className="text-lg font-semibold font-mono">{p.yearsActive}</div>
          </Card>
        </div>

        {/* Portfolio */}
        {p.portfolio?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold tracking-tight mb-3">{t("producer.portfolio")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {p.portfolio.map((src, i) => (
                <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden border border-carbon-600 group cursor-pointer">
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-24">
          <Section title={t("producer.skills")} icon={Wrench}>
            <div className="flex flex-wrap gap-1.5">
              {p.skills.map((s) => <Chip key={s}>{s}</Chip>)}
            </div>
          </Section>
          <Section title={t("producer.materials")} icon={Package}>
            <div className="flex flex-wrap gap-1.5">
              {p.materials.map((m) => <Chip key={m}>{m}</Chip>)}
            </div>
          </Section>
          <Section title={t("producer.machines")} icon={Boxes}>
            <div className="space-y-1.5">
              {p.machines?.length ? p.machines.map((m) => (
                <div key={m} className="text-xs text-ink-700 font-mono pl-3 border-l-2 border-electric-500/40">{m}</div>
              )) : <p className="text-xs text-ink-600">—</p>}
            </div>
          </Section>
          {p.certifications?.length > 0 ? (
            <Section title={t("producer.certifications")} icon={Award}>
              <div className="space-y-1.5">
                {p.certifications.map((c) => (
                  <div key={c} className="text-xs text-ink-700 flex items-center gap-2">
                    <Award className="w-3 h-3 text-electric-400" /> {c}
                  </div>
                ))}
              </div>
            </Section>
          ) : (
            <Section title={t("producer.certifications")} icon={Award}>
              <p className="text-xs text-ink-600">—</p>
            </Section>
          )}
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-30 bg-carbon-900/90 backdrop-blur-xl border-t border-carbon-600 p-3">
        <div className="max-w-4xl mx-auto flex gap-2">
          <Button variant="secondary" className="flex-1" data-testid="producer-contact" onClick={startChat} disabled={starting}>
            <MessageSquare className="w-4 h-4" />
            {t("producer.contact")}
          </Button>
          <Button className="flex-1" data-testid="producer-invite" onClick={startChat} disabled={starting}>
            <Send className="w-4 h-4" />
            {t("producer.invite")}
          </Button>
        </div>
      </div>
    </div>
  );
}
